/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import { ChatCircle, Heart } from "@phosphor-icons/react";
import ReactionPicker, {
	REACTIONS
} from "src/features/SocialFeedContent/components/ReactionPicker/ReactionPicker";
import useReactToPost from "src/features/SocialFeedContent/hooks/useReactToPost/useReactToPost";
import useRemoveReaction from "src/features/SocialFeedContent/hooks/useRemoveReaction/useRemoveReaction";

interface Props {
	postId: string;
	reactionsCount: number;
	commentsCount: number;
	isExpanded: boolean;
	onToggleComments: () => void;
	userReaction?: string | null;
}

const PostEngagementBar: FC<Props> = ({
	postId,
	reactionsCount,
	commentsCount,
	isExpanded,
	onToggleComments,
	userReaction
}) => {
	const [showPicker, setShowPicker] = useState(false);
	const [animating, setAnimating] = useState(false);
	const pickerRef = useRef<HTMLDivElement>(null);
	const reactMutation = useReactToPost();
	const removeMutation = useRemoveReaction();

	const hasReacted = Boolean(userReaction);
	const activeReaction = REACTIONS.find((r) => r.value === userReaction);

	useEffect(() => {
		if (!showPicker) return;

		const handleClickOutside = (e: MouseEvent) => {
			if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
				setShowPicker(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [showPicker]);

	const handleReact = (reaction: string) => {
		reactMutation.mutate({ social_post: postId, reaction });
	};

	const handleToggleReaction = (e: React.MouseEvent) => {
		e.stopPropagation();
		setAnimating(true);
		setTimeout(() => setAnimating(false), 300);
		if (hasReacted) {
			removeMutation.mutate(postId);
		} else {
			handleReact("like");
		}
	};

	const handlePickerToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		setShowPicker((prev) => !prev);
	};

	return (
		<div className="px-4 py-2.5 flex items-center justify-between border-t border-border">
			<div className="flex items-center gap-3">
				<div ref={pickerRef} className="relative flex items-center">
					<button
						type="button"
						aria-label={hasReacted ? "Remove reaction" : "React with like"}
						onClick={handleToggleReaction}
						className={[
							"inline-flex items-center gap-1 text-[13px] transition-all",
							animating ? "scale-125" : "scale-100",
							hasReacted
								? "text-primary font-medium"
								: "text-muted-foreground hover:text-foreground"
						].join(" ")}
					>
						{activeReaction ? (
							<span className="text-base leading-none">
								{activeReaction.emoji}
							</span>
						) : (
							<Heart size={14} weight="duotone" />
						)}
					</button>

					<button
						type="button"
						aria-label="Choose reaction"
						onClick={handlePickerToggle}
						className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors text-[10px] leading-none"
					>
						&#9662;
					</button>

					{showPicker && (
						<ReactionPicker
							onReact={handleReact}
							onClose={() => setShowPicker(false)}
						/>
					)}
				</div>

				{reactionsCount > 0 && (
					<span className="text-[13px] text-muted-foreground">
						{reactionsCount} {reactionsCount === 1 ? "reaction" : "reactions"}
					</span>
				)}
			</div>

			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onToggleComments();
				}}
				className={[
					"inline-flex items-center gap-1.5 text-[13px] transition-colors",
					isExpanded
						? "text-primary font-medium"
						: "text-muted-foreground hover:text-foreground"
				].join(" ")}
			>
				<ChatCircle size={14} weight="duotone" />
				{commentsCount > 0
					? `${commentsCount} ${commentsCount === 1 ? "comment" : "comments"}`
					: "Comment"}
			</button>
		</div>
	);
};

export default PostEngagementBar;
